import {Role} from "@/src/modules/users/types";

import {meApi} from "../api/me";
import {
  flattenGroupedPermissions,
  hasGroupedPermission,
  normalizeGroupedPermissions,
} from "../lib/permissions";
import {getMeHouseholdId} from "../lib/get-me-household-id";
import CoreService from "../services/core";
import {useMeStore} from "../store/me";
import type {Me} from "../types";
import {userPropertyService} from "./user-property";

class MeService extends CoreService {
  async getMe(): Promise<Me> {
    const response = await meApi.get();
    const payload = (response.data as unknown as {data: Me & {roles?: Role[]; role?: Role}}).data;
    const roles = payload.roles ?? (payload.role ? [payload.role] : []);
    const role = roles[0] ?? payload.role;
    if (!role) {
      throw new Error("User profile is missing a role");
    }
    const {roles: _roles, role: _role, ...rest} = payload as Me & {
      roles?: Role[];
      role?: Role;
      house_hold?: {id?: number; name?: string} | null;
    };

    const normalizeRoleName = (name: string) => name.trim().toLowerCase();

    const me = {
      ...rest,
      role,
      house_hold_id: getMeHouseholdId(rest),
      effective_permissions: payload.effective_permissions
        ? normalizeGroupedPermissions(payload.effective_permissions)
        : undefined,
      direct_permissions: payload.direct_permissions
        ? normalizeGroupedPermissions(payload.direct_permissions)
        : undefined,
      impersonation: payload.impersonation ?? null,
      isAdmin: roles.some((r) => normalizeRoleName(r.name) === "admin"),
      isSuperAdmin: roles.some((r) => normalizeRoleName(r.name) === "super_admin"),
      isSupervisor: normalizeRoleName(role.name) === "supervisor",
      isSeniorSupervisor: normalizeRoleName(role.name) === "senior supervisor",
      isPostCommander: normalizeRoleName(role.name) === "post commander",
      isPropertyManager: normalizeRoleName(role.name) === "property manager",
      isDispatch: normalizeRoleName(role.name) === "dispatch",
      isDispatchSupervisor: normalizeRoleName(role.name) === "dispatch supervisor",
      isOfficer: normalizeRoleName(role.name) === "officer",
      isResident: normalizeRoleName(role.name) === "resident",
      isPatrolOfficer: normalizeRoleName(role.name) === "patrol officer",
    };

    useMeStore.setState({
      me,
    });

    userPropertyService.initUserProperty(me);

    return me;
  }

  clearMe(): void {
    useMeStore.setState({
      me: null,
    });
  }

  getHasPermission(permission: string): boolean {
    const me = useMeStore.getState().me;
    if (!me) return false;
    if (me.effective_permissions) {
      return hasGroupedPermission(me.effective_permissions, permission);
    }
    return me.role.permissions.some((p) => p.name === permission);
  }

  getEffectivePermissionNames(): Set<string> {
    const me = useMeStore.getState().me;
    if (!me) return new Set();
    if (me.effective_permissions) {
      return flattenGroupedPermissions(me.effective_permissions);
    }
    return new Set(me.role.permissions.map((p) => p.name));
  }
}

export const meService = new MeService();
